// Suggested ways to split an assessment into mark sheet sections, for teachers who are not sure
// how to break up their paper. Every suggestion is a starting point the teacher edits.

const MANY_QUESTIONS = 12;

/** Split a total over shares (percentages) in whole marks that add up exactly (largest remainder). */
export function splitMarks(shares, total) {
  const whole = Math.max(0, Math.round(Number(total) || 0));
  const sum = shares.reduce((acc, share) => acc + share, 0) || 1;
  const exact = shares.map((share) => (share / sum) * whole);
  const marks = exact.map(Math.floor);
  let left = whole - marks.reduce((acc, mark) => acc + mark, 0);
  exact
    .map((value, index) => ({ index, rest: value - Math.floor(value) }))
    .sort((a, b) => b.rest - a.rest)
    .forEach(({ index }) => {
      if (left > 0) { marks[index] += 1; left -= 1; }
    });
  return marks;
}

// ALL-CAPS headings become sentence case, keeping section letters and "3D" upright.
const sentenceCase = (text) => (text === text.toUpperCase()
  ? text.toLowerCase()
    .replace(/(^|:\s+|\(\s*)(\p{L})/gu, (match, before, letter) => before + letter.toUpperCase())
    .replace(/\b(\p{L})(?=\s*(?::|$|\)))/gu, (letter) => letter.toUpperCase())
    .replace(/\b(\d)([a-z])\b/g, (match, digit, letter) => digit + letter.toUpperCase())
  : text);

/** "VRAAG 1: LEESBEGRIP - STORIE (15 PUNTE)" -> "Vraag 1: Leesbegrip - Storie". */
export function cleanSectionName(name) {
  const withoutMarks = String(name || '')
    .replace(/[([]\s*\d+\s*(?:marks?|punte|punt|pts?)?\s*[)\]]/giu, '')
    .replace(/\s+/g, ' ')
    .replace(/[\s:–-]+$/u, '')
    .trim();
  return sentenceCase(withoutMarks);
}

/**
 * The ways to split a paper read by suggest_sections.php ({ total, sections, questions }):
 * by lettered section, by main question, or one mark for the whole paper. The server only
 * returns a split whose marks add up to the paper total. Defaults to the most useful one.
 */
export function viewsFromPaper(paper) {
  const total = Number(paper?.total) || 0;
  const clean = (rows) => (Array.isArray(rows) ? rows : [])
    .map((row) => ({ name: cleanSectionName(row.name), max: Number(row.max) || 0 }))
    .filter((row) => row.name && row.max > 0);
  const views = [];
  const sections = clean(paper?.sections);
  const questions = clean(paper?.questions);
  if (sections.length > 1) views.push({ id: 'sections', label: 'By section', rows: sections });
  if (questions.length > 1) views.push({ id: 'questions', label: 'By question', rows: questions });
  views.push({ id: 'whole', label: 'Whole paper', rows: [{ name: 'Whole paper', max: total }] });
  // Lots of one-mark questions (like an ANA paper) are easier as one column.
  const usable = views.filter((view) => view.id !== 'whole' && view.rows.length <= MANY_QUESTIONS);
  const preferred = usable.find((view) => view.id === 'questions') || usable[0] || views[views.length - 1];
  return { total, views, defaultView: preferred.id, tooMany: views.some((v) => v.rows.length > MANY_QUESTIONS) };
}

const LANGUAGE = /english|afrikaans|isizulu|isixhosa|sesotho|setswana|sepedi|xitsonga|siswati|tshivenda|isindebele|home language|first additional|\bhl\b|\bfal\b|language/i;

/** Common layouts for this subject, each a list of { name, share } to split the total over. */
export function templatesForSubject(subjectName, grade) {
  const subject = String(subjectName || '');
  const gradeNumber = Number(String(grade || '').match(/\d+/)?.[0]);
  const templates = [];
  if (/math|wiskunde/i.test(subject) && gradeNumber >= 4 && gradeNumber <= 6) {
    templates.push({
      id: 'maths-ip',
      label: 'By CAPS content area',
      description: 'The five Intermediate Phase content areas, split by the CAPS weighting.',
      parts: [
        { name: 'Numbers, operations and relationships', share: 50 },
        { name: 'Patterns, functions and algebra', share: 10 },
        { name: 'Space and shape', share: 15 },
        { name: 'Measurement', share: 15 },
        { name: 'Data handling', share: 10 },
      ],
    });
  }
  if (LANGUAGE.test(subject)) {
    const afrikaans = /afrikaans/i.test(subject);
    templates.push({
      id: 'language-paper',
      label: afrikaans ? 'Taalvraestel' : 'Language paper',
      description: 'Comprehension, visual text, summary and language, as most language papers are set out.',
      parts: afrikaans
        ? [{ name: 'Leesbegrip', share: 40 }, { name: 'Visuele teks', share: 20 }, { name: 'Opsomming', share: 10 }, { name: 'Taalstrukture en -konvensies', share: 30 }]
        : [{ name: 'Reading comprehension', share: 40 }, { name: 'Visual text', share: 20 }, { name: 'Summary', share: 10 }, { name: 'Language structures and conventions', share: 30 }],
    });
  }
  if (/social|sosiale|history|geskiedenis|geography|geografie/i.test(subject)) {
    templates.push({
      id: 'ss',
      label: 'History and Geography',
      description: 'One section for each half of the paper.',
      parts: [{ name: 'History', share: 50 }, { name: 'Geography', share: 50 }],
    });
  }
  templates.push({
    id: 'a-b',
    label: 'Section A and Section B',
    description: 'Short questions (multiple choice, matching, true or false), then longer questions.',
    parts: [{ name: 'Section A: Short questions', share: 40 }, { name: 'Section B: Longer questions', share: 60 }],
  });
  templates.push({
    id: 'questions',
    label: 'One section per question',
    description: 'Question 1, Question 2 and so on. Choose how many questions your paper has.',
    questionCount: 4,
  });
  return templates;
}

/** The rows a template produces for a total (and question count, for the per-question layout). */
export function rowsFromTemplate(template, total, questionCount = template?.questionCount || 4) {
  const parts = template.questionCount
    ? Array.from({ length: Math.max(1, Math.min(30, questionCount)) }, (_, index) => ({ name: `Question ${index + 1}`, share: 1 }))
    : template.parts;
  const marks = splitMarks(parts.map((part) => part.share), total);
  return parts.map((part, index) => ({ name: part.name, max: marks[index] }));
}
